import React, { useState, useEffect, useCallback } from 'react';
import { 
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, PhoneOff,
  RefreshCw, Loader2, User, Building2, Clock, Calendar
} from 'lucide-react';
import type { Team, SalesRoute } from '../../types';
import { supabase } from '../../services/supabase';

interface CallsPageProps {
  team: Team;
  navigate: (route: SalesRoute, params?: Record<string, string>) => void;
}

interface CallRecord {
  id: string;
  contact_id: string | null;
  contact_name: string | null;
  company: string | null;
  direction: 'inbound' | 'outbound';
  from_number: string;
  to_number: string;
  status: string;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  recording_url: string | null;
}

type CallFilter = 'all' | 'inbound' | 'outbound' | 'missed';

const CallsPage: React.FC<CallsPageProps> = ({ team, navigate }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [filter, setFilter] = useState<CallFilter>('all');

  const fetchCalls = useCallback(async () => {
    const { data, error } = await supabase
      .from('sales_calls')
      .select(`
        id,
        contact_id,
        direction,
        from_number,
        to_number,
        status,
        started_at,
        ended_at,
        duration_seconds,
        recording_url,
        sales_contacts (
          name,
          company
        )
      `)
      .eq('org_id', team.id)
      .order('started_at', { ascending: false })
      .limit(100);

    if (error) {
      console.error('[calls] Fetch failed:', error);
      return;
    }

    const formatted = (data || []).map((call: any) => ({
      ...call,
      contact_name: call.sales_contacts?.name || null,
      company: call.sales_contacts?.company || null,
    }));

    setCalls(formatted);
  }, [team.id]);

  useEffect(() => {
    setLoading(true);
    fetchCalls().finally(() => setLoading(false));
  }, [fetchCalls]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCalls();
    setRefreshing(false);
  };

  const handleContactClick = (contactId: string | null) => {
    if (contactId) {
      navigate('leads', { contactId, tab: 'comms' });
    }
  };

  const handleCall = (number: string) => {
    window.dispatchEvent(new CustomEvent('initiate-call', { detail: { number } }));
  };

  const filteredCalls = calls.filter((call) => {
    if (filter === 'all') return true;
    if (filter === 'inbound') return call.direction === 'inbound';
    if (filter === 'outbound') return call.direction === 'outbound';
    if (filter === 'missed') {
      return call.direction === 'inbound' && 
        (call.status === 'no-answer' || call.status === 'busy' || call.status === 'canceled');
    }
    return true;
  });

  const stats = {
    total: calls.length,
    inbound: calls.filter(c => c.direction === 'inbound').length,
    outbound: calls.filter(c => c.direction === 'outbound').length,
    missed: calls.filter(c => 
      c.direction === 'inbound' && 
      (c.status === 'no-answer' || c.status === 'busy' || c.status === 'canceled')
    ).length,
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusIcon = (call: CallRecord) => {
    if (call.direction === 'inbound' && 
        (call.status === 'no-answer' || call.status === 'busy' || call.status === 'canceled')) {
      return <PhoneMissed size={16} className="text-red-400" />;
    }
    if (call.direction === 'inbound') {
      return <PhoneIncoming size={16} className="text-green-400" />;
    }
    if (call.status === 'completed') {
      return <PhoneOutgoing size={16} className="text-blue-400" />;
    }
    return <PhoneOff size={16} className="text-slate-400" />;
  };

  const getStatusText = (call: CallRecord): string => {
    if (call.status === 'completed') {
      return call.duration_seconds ? formatDuration(call.duration_seconds) : 'Completed';
    }
    if (call.status === 'no-answer') return 'No answer';
    if (call.status === 'busy') return 'Busy';
    if (call.status === 'canceled') return 'Cancelled';
    if (call.status === 'failed') return 'Failed';
    return call.status || 'Unknown';
  };

  const getStatusColor = (call: CallRecord): string => {
    if (call.status === 'completed') return 'text-green-400';
    if (call.status === 'no-answer' || call.status === 'busy' || call.status === 'canceled') {
      return call.direction === 'inbound' ? 'text-red-400' : 'text-yellow-400';
    }
    if (call.status === 'failed') return 'text-red-400';
    return 'text-slate-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Phone size={24} className="text-orange-500" />
            Calls
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Call history and activity
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <button
          onClick={() => setFilter('all')}
          className={`bg-slate-900 rounded-xl border p-4 text-left transition-colors ${
            filter === 'all' ? 'border-orange-500' : 'border-slate-800 hover:border-slate-700'
          }`}
        >
          <div className="text-2xl font-bold text-white">{stats.total}</div>
          <div className="text-sm text-slate-400">Total Calls</div>
        </button>
        <button
          onClick={() => setFilter('inbound')}
          className={`bg-slate-900 rounded-xl border p-4 text-left transition-colors ${
            filter === 'inbound' ? 'border-green-500' : 'border-slate-800 hover:border-slate-700'
          }`}
        >
          <div className="text-2xl font-bold text-green-400">{stats.inbound}</div>
          <div className="text-sm text-slate-400">Inbound</div>
        </button>
        <button
          onClick={() => setFilter('outbound')}
          className={`bg-slate-900 rounded-xl border p-4 text-left transition-colors ${
            filter === 'outbound' ? 'border-blue-500' : 'border-slate-800 hover:border-slate-700'
          }`}
        >
          <div className="text-2xl font-bold text-blue-400">{stats.outbound}</div>
          <div className="text-sm text-slate-400">Outbound</div>
        </button>
        <button
          onClick={() => setFilter('missed')}
          className={`bg-slate-900 rounded-xl border p-4 text-left transition-colors ${
            filter === 'missed' ? 'border-red-500' : 'border-slate-800 hover:border-slate-700'
          }`}
        >
          <div className="text-2xl font-bold text-red-400">{stats.missed}</div>
          <div className="text-sm text-slate-400">Missed</div>
        </button>
      </div>

      {/* Call List */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
          <h2 className="font-semibold text-white flex items-center gap-2">
            <Clock size={18} className="text-orange-500" />
            {filter === 'all' ? 'All Calls' : 
             filter === 'inbound' ? 'Inbound Calls' :
             filter === 'outbound' ? 'Outbound Calls' : 'Missed Calls'}
          </h2>
          <span className="text-sm text-slate-400">{filteredCalls.length} calls</span>
        </div>

        {filteredCalls.length === 0 ? (
          <div className="p-8 text-center">
            <Phone size={48} className="mx-auto mb-4 text-slate-600" />
            <h3 className="text-lg font-medium text-white mb-2">No calls yet</h3>
            <p className="text-slate-400 text-sm">
              Call history will appear here when you make or receive calls.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {filteredCalls.map((call) => (
              <div
                key={call.id}
                className="px-5 py-4 flex items-center gap-4 hover:bg-slate-800/50 transition-colors"
              >
                {/* Direction Icon */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                  call.direction === 'inbound' && (call.status === 'no-answer' || call.status === 'busy')
                    ? 'bg-red-500/20'
                    : call.direction === 'inbound'
                    ? 'bg-green-500/20'
                    : 'bg-blue-500/20'
                }`}>
                  {getStatusIcon(call)}
                </div>

                {/* Contact Info */}
                <div className="flex-1 min-w-0">
                  {call.contact_id && call.contact_name ? (
                    <button
                      onClick={() => handleContactClick(call.contact_id)}
                      className="text-left hover:underline"
                    >
                      <div className="font-medium text-white">{call.contact_name}</div>
                      {call.company && (
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Building2 size={12} />
                          {call.company}
                        </div>
                      )}
                    </button>
                  ) : (
                    <div className="font-medium text-white">
                      {call.direction === 'inbound' ? call.from_number : call.to_number}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                    <Calendar size={12} />
                    {new Date(call.started_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>

                {/* Status */}
                <div className="text-right shrink-0">
                  <div className={`text-sm font-medium ${getStatusColor(call)}`}>
                    {getStatusText(call)}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {call.direction === 'inbound' ? 'Inbound' : 'Outbound'}
                  </div>
                </div>

                {/* Call Back Button */}
                <button
                  onClick={() => handleCall(call.direction === 'inbound' ? call.from_number : call.to_number)}
                  className="p-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors shrink-0"
                  title="Call back"
                >
                  <Phone size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CallsPage;
